import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

const Terms = () => (
  <div className="min-h-screen bg-[#FDFBF7] paper-texture">
    <nav className="bg-white/80 backdrop-blur-xl border-b border-[#E1D9CD]">
      <div className="container mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <BookOpen className="w-6 h-6 text-[#6B7A4F]" />
          <span className="text-xl font-bold text-[#2D2A26]">Bookish Bestie</span>
        </Link>
        <Link to="/">
          <Button variant="ghost" size="sm" className="text-[#5C5955]">
            <ArrowLeft className="w-4 h-4 mr-2" />Home
          </Button>
        </Link>
      </div>
    </nav>

    <div className="container mx-auto px-6 py-12 max-w-3xl">
      <div className="glass-card p-8 sm:p-12 rounded-2xl">
        <div className="text-center mb-8">
          <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#6B7A4F]">A Built for Bookish Experience</span>
          <h1 className="text-4xl font-light text-[#2D2A26] mt-3">Terms of <span className="font-bold">Service</span></h1>
          <p className="text-sm text-[#5C5955] mt-2">Last updated: February 2026</p>
        </div>

        <div className="prose prose-sm max-w-none text-[#2D2A26] space-y-6">
          <section>
            <h2 className="text-2xl font-semibold mb-3">1. Welcome to Bookish Bestie</h2>
            <p className="text-[#5C5955] leading-relaxed">
              By creating an account or using Bookish Bestie ("the Service"), you agree to these Terms of Service.
              If you do not agree, please do not use the Service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">2. How Matches Work</h2>
            <p className="text-[#5C5955] leading-relaxed">
              Matches are generated based on your <strong>location</strong> and the <strong>reading preferences,
              favorite genres, and books</strong> you provide in your profile. Our algorithm uses this information
              along with AI-powered analysis to suggest compatible users.
            </p>
            <p className="text-[#5C5955] leading-relaxed mt-3">
              <strong>Matches cannot be guaranteed to become bookish besties.</strong> Compatibility scores are
              suggestions only. Whether a match blossoms into a friendship depends entirely on you and the other user.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">3. In-Person Meetings & Safety</h2>
            <p className="text-[#5C5955] leading-relaxed">
              Bookish Bestie encourages real-life meetups at coffee shops, bookstores, and other public venues
              through our Book Clubs feature. However:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-[#5C5955] mt-3">
              <li>
                <strong>You meet publicly at your own risk.</strong> Always meet in well-lit, populated public
                locations. Never share personal financial or home address information with matches.
              </li>
              <li>
                <strong>We are not liable for any incidents, injuries, losses, or damages</strong> that may occur
                during meetups, interactions, or exchanges arranged through the Service — whether online or in person.
              </li>
              <li>
                <strong>We do not conduct background checks</strong> on users. You are responsible for exercising
                your own judgment when interacting with other users.
              </li>
              <li>
                By using this Service, you release Bookish Bestie and its operators from any and all claims,
                demands, or damages arising from your interactions with other users.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">4. Payment & No Refund Policy</h2>
            <div className="bg-[#E2E6D5]/40 border-l-4 border-[#6B7A4F] p-4 rounded-r-lg mb-3">
              <p className="text-[#2D2A26] font-semibold">One-time payment of $10 USD. All sales are final. No refunds under any circumstances.</p>
            </div>
            <p className="text-[#5C5955] leading-relaxed">
              Bookish Bestie charges a <strong>single, one-time payment of $10 USD</strong> for access to the Service.
              This is <strong>not a subscription</strong>. You will not be billed again after your initial purchase.
              Once purchased, you gain <strong>immediate access</strong> to matches, messaging, book clubs, and all
              other paid features. Because you receive value the moment you pay, we operate under a strict
              <strong> no-refund policy</strong>. By completing your purchase, you acknowledge and accept that:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-[#5C5955] mt-3">
              <li>The $10 fee is a <strong>one-time, non-recurring payment</strong>. No subscriptions or hidden fees.</li>
              <li>All sales are <strong>final and non-refundable</strong> for any reason whatsoever.</li>
              <li>No refunds will be granted for dissatisfaction, unused features, changes in circumstances, account termination, or any other reason.</li>
              <li>Chargebacks may result in permanent account termination and may be disputed on our behalf.</li>
              <li>Prices are subject to change for new users, but your one-time payment covers only the pricing in effect at the time you purchased.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">5. Service Availability, Modification & Termination</h2>
            <div className="bg-[#E2E6D5]/40 border-l-4 border-[#6B7A4F] p-4 rounded-r-lg mb-3">
              <p className="text-[#2D2A26] font-semibold">
                Your one-time payment grants access for as long as the Service remains operational. It does NOT
                guarantee access in perpetuity if the Service is discontinued.
              </p>
            </div>
            <p className="text-[#5C5955] leading-relaxed">
              By purchasing access to Bookish Bestie, you understand and expressly agree that:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-[#5C5955] mt-3">
              <li>
                <strong>"Lifetime access" refers to the lifetime of the Service, not the user.</strong> Access is
                granted for as long as Bookish Bestie remains available and operational.
              </li>
              <li>
                <strong>We reserve the sole and absolute right to modify, suspend, or permanently discontinue the
                Service, in whole or in part, at any time, for any reason or no reason, with or without notice.</strong>
                Reasons may include but are not limited to: financial viability, technical constraints, changes in
                business direction, legal or regulatory requirements, or personal circumstances of the operators.
              </li>
              <li>
                In the event that Bookish Bestie is discontinued, shut down, sold, transferred, or otherwise ceases
                operation, <strong>you shall NOT be entitled to any refund, credit, compensation, or reimbursement
                of any kind</strong>, regardless of how recently you purchased access or how much of the Service you
                used prior to shutdown.
              </li>
              <li>
                We may attempt (but are not required) to provide advance notice via email or in-app notification
                before permanent shutdown. Failure to provide notice does not create any right to refund or compensation.
              </li>
              <li>
                We may also terminate or restrict individual user accounts at our sole discretion for violation of
                these Terms, without refund, notice, or explanation.
              </li>
              <li>
                We are not obligated to preserve, export, or return any user data — including messages, profile
                information, connections, book libraries, or book club activity — upon discontinuation or account
                termination. You are responsible for saving anything you wish to keep.
              </li>
              <li>
                You waive any and all claims against Bookish Bestie, its operators, employees, contractors, and
                affiliates related to the discontinuation, unavailability, or termination of the Service.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">6. User Conduct</h2>
            <p className="text-[#5C5955] leading-relaxed">
              You agree not to use the Service to harass, abuse, threaten, impersonate, or spam other users.
              Accounts violating these rules may be suspended or terminated without notice or refund.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">7. Limitation of Liability</h2>
            <p className="text-[#5C5955] leading-relaxed">
              To the fullest extent permitted by law, Bookish Bestie, its operators, employees, and affiliates
              shall not be liable for any indirect, incidental, consequential, or punitive damages, or any loss
              of profits, data, or goodwill arising out of your use of the Service, matches, meetups, discontinuation
              of the Service, or any interactions between users. You agree that our maximum liability shall not
              exceed the amount you paid for the Service ($10).
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">8. Changes to These Terms</h2>
            <p className="text-[#5C5955] leading-relaxed">
              We may update these Terms from time to time. Continued use of the Service after changes constitutes
              acceptance of the updated Terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">9. Contact</h2>
            <p className="text-[#5C5955] leading-relaxed">
              Questions? Reach out via our social channels @BuiltForBookish.
            </p>
          </section>
        </div>
      </div>
    </div>
  </div>
);

export default Terms;
